import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownUp,
  CakeSlice,
  CheckCircle2,
  ChefHat,
  ChevronRight,
  Coffee,
  Copy,
  CupSoda,
  EyeOff,
  GripVertical,
  ImagePlus,
  Martini,
  MoreHorizontal,
  Plus,
  RotateCcw,
  Save,
  Search,
  Soup,
  Trash2,
  Tv,
  Upload,
  UtensilsCrossed,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type Site } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { ask } from '../components/Confirm';
import { useAssetUrls } from '../components/EntityForm';
import { Menu } from '../components/Menu';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Loading, PageHeader, Toggle } from '../components/ui';
import type { Venue } from './RestaurantsPage';

interface Dish {
  key: string;
  name: string;
  description: string;
  price: string;
  imageAssetId: string | null;
  allergens: string[];
  available: boolean;
}
interface Section {
  key: string;
  name: string;
  items: Dish[];
}

let seq = 0;
const newKey = () => `k${++seq}`;

/** A section icon picked from its name (Starters, Desserts, Drinks…). */
function sectionIcon(name: string): LucideIcon {
  const n = name.toLowerCase();
  if (/dessert|sweet|cake|pastr/.test(n)) return CakeSlice;
  if (/cocktail|wine|bar|spirit|beer/.test(n)) return Martini;
  if (/coffee|tea|breakfast/.test(n)) return Coffee;
  if (/drink|beverage|juice|soft/.test(n)) return CupSoda;
  if (/soup/.test(n)) return Soup;
  if (/main|grill|course/.test(n)) return ChefHat;
  return UtensilsCrossed;
}

const ALLERGEN_TINT: Record<string, [string, string]> = {
  dairy: ['#b45309', '#fff4e0'],
  gluten: ['#a16207', '#fef9c3'],
  shellfish: ['#be123c', '#ffe9ee'],
  nuts: ['#92400e', '#fdf0e1'],
  egg: ['#ca8a04', '#fefce8'],
  fish: ['#1d4ed8', '#e8f0ff'],
  soy: ['#4d7c0f', '#f0f9e3'],
};
const tint = (a: string) => ALLERGEN_TINT[a.toLowerCase()] ?? ['#475569', '#eef1f5'];

function StatCard({ icon, tone, value, label }: { icon: ReactNode; tone: string; value: number; label: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
      </span>
    </div>
  );
}

export function VenueMenuPage() {
  const site = useSite();
  const { venueId } = useParams();
  return <NeedsSite>{site && venueId ? <MenuLoader key={venueId} site={site} venueId={venueId} /> : <PageHeader title="Edit menu" />}</NeedsSite>;
}

function MenuLoader({ site, venueId }: { site: Site; venueId: string }) {
  const venues = useQuery({ queryKey: ['venues', site.id], queryFn: () => api.get<{ items: Venue[] }>(`/sites/${site.id}/venues`).then((r) => r.items) });
  const navigate = useNavigate();
  if (venues.isLoading) return <Loading />;
  const venue = venues.data?.find((v) => v.id === venueId);
  if (!venue)
    return (
      <>
        <PageHeader title="Edit menu" />
        <div className="card empty">
          This venue was not found in {site.name}.{' '}
          <button className="btn small" onClick={() => navigate('/experience/restaurants')}>
            Back to Restaurants & Bars
          </button>
        </div>
      </>
    );
  return <MenuEditor site={site} venue={venue} />;
}

const fromVenue = (v: Venue): Section[] =>
  v.sections.map((s) => ({
    key: newKey(),
    name: s.name,
    items: s.items.map((i) => ({
      key: newKey(),
      name: i.name,
      description: i.description ?? '',
      price: i.price === null ? '' : Number(i.price).toFixed(2),
      imageAssetId: i.imageAssetId,
      allergens: i.allergens,
      available: i.available,
    })),
  }));

function MenuEditor({ site, venue }: { site: Site; venue: Venue }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const url = useAssetUrls();
  const editable = can('ADMIN');
  const [sections, setSections] = useState<Section[]>(() => fromVenue(venue));
  const [dirty, setDirty] = useState(false);
  const [active, setActive] = useState(0);
  const [q, setQ] = useState('');
  const [show, setShow] = useState('all');
  const [sort, setSort] = useState('order');
  const [selKey, setSelKey] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [dragDish, setDragDish] = useState<number | null>(null);
  const [dragSec, setDragSec] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const change = (next: Section[]) => {
    setSections(next);
    setDirty(true);
    setSaved(false);
  };
  const section = sections[active];
  const setItems = (items: Dish[]) => change(sections.map((s, i) => (i === active ? { ...s, items } : s)));
  const setDish = (key: string, patch: Partial<Dish>) => section && setItems(section.items.map((d) => (d.key === key ? { ...d, ...patch } : d)));

  const save = useMutation({
    mutationFn: () =>
      api.put(`/venues/${venue.id}/menu`, {
        sections: sections
          .filter((s) => s.name.trim())
          .map((s) => ({
            name: s.name.trim(),
            items: s.items
              .filter((i) => i.name.trim())
              .map((i) => ({
                name: i.name.trim(),
                description: i.description.trim() || null,
                price: i.price === '' ? null : Number(i.price),
                imageAssetId: i.imageAssetId,
                allergens: i.allergens,
                available: i.available,
              })),
          })),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['venues', site.id] });
      setDirty(false);
      setSaved(true);
    },
  });

  const all = sections.flatMap((s) => s.items);
  const items = section?.items ?? [];
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = items.filter(
      (d) => (!s || d.name.toLowerCase().includes(s) || d.description.toLowerCase().includes(s)) && (show === 'all' || (show === 'available' ? d.available : !d.available)),
    );
    if (sort === 'name') return [...list].sort((a, b) => a.name.localeCompare(b.name));
    if (sort === 'price') return [...list].sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
    return list;
  }, [items, q, show, sort]);
  const canDrag = editable && sort === 'order' && !q.trim() && show === 'all';
  const sel = items.find((d) => d.key === selKey) ?? shown[0] ?? null;
  const pageKeys = shown.map((d) => d.key);
  const allChecked = pageKeys.length > 0 && pageKeys.every((k) => checked.has(k));

  const addDish = () => {
    const d: Dish = { key: newKey(), name: '', description: '', price: '', imageAssetId: null, allergens: [], available: true };
    setItems([...items, d]);
    setSelKey(d.key);
    setQ('');
    setShow('all');
    setTimeout(() => document.querySelector<HTMLInputElement>(`[data-dish="${d.key}"]`)?.focus(), 50);
  };
  const addSection = () => {
    change([...sections, { key: newKey(), name: 'New section', items: [] }]);
    setActive(sections.length);
    setChecked(new Set());
    setTimeout(() => document.querySelector<HTMLInputElement>('.menu-sec-title')?.select(), 50);
  };
  const moveDishTo = (key: string, target: number) => {
    const d = items.find((x) => x.key === key);
    if (!d) return;
    change(sections.map((s, i) => (i === active ? { ...s, items: s.items.filter((x) => x.key !== key) } : i === target ? { ...s, items: [...s.items, d] } : s)));
  };
  const leave = () => {
    if (dirty) ask('Leave without saving? Your menu changes will be lost.', () => navigate('/experience/restaurants'), { action: 'Leave', danger: true });
    else navigate('/experience/restaurants');
  };

  return (
    <>
      <div className="crumbs">
        <button type="button" onClick={leave}>
          Restaurants & Bars
        </button>
        <ChevronRight size={14} />
        <button type="button" onClick={leave}>
          {venue.name}
        </button>
        <ChevronRight size={14} />
        <strong>Menu editor</strong>
      </div>
      <PageHeader
        title={`Edit menu · ${venue.name}`}
        sub="Manage the menu sections and dishes. Guests see them on the TV when they open this venue."
        actions={
          editable && (
            <>
              {saved && !dirty && <span className="badge ok">Saved · on the TVs</span>}
              {dirty && <span className="badge warn">Unsaved changes</span>}
              <button
                className="btn"
                disabled={!dirty}
                onClick={() =>
                  ask('Discard your changes? The menu goes back to the last saved version.', () => {
                    setSections(fromVenue(venue));
                    setDirty(false);
                    setActive(0);
                  }, { action: 'Discard', danger: true })
                }
              >
                <RotateCcw size={16} /> Discard
              </button>
              <button className="btn primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                <Save size={16} /> {save.isPending ? 'Saving…' : 'Save menu'}
              </button>
            </>
          )
        }
      />
      <ErrorBox error={save.error} />
      <div className="stat-strip">
        <StatCard icon={<UtensilsCrossed size={22} />} tone="violet" value={sections.length} label="Total sections" />
        <StatCard icon={<ChefHat size={22} />} tone="blue" value={all.length} label="Total dishes" />
        <StatCard icon={<CheckCircle2 size={22} />} tone="green" value={all.filter((d) => d.available).length} label="Available dishes" />
        <StatCard icon={<EyeOff size={22} />} tone="red" value={all.filter((d) => !d.available).length} label="Hidden dishes" />
      </div>

      <div className="menu-editor">
        <div className="card menu-sections">
          <h3 style={{ margin: 0 }}>Menu sections</h3>
          {editable && (
            <button className="btn primary" style={{ width: '100%', justifyContent: 'center' }} onClick={addSection}>
              <Plus size={16} /> New section
            </button>
          )}
          {sections.map((s, i) => {
            const Icon = sectionIcon(s.name);
            return (
              <button
                key={s.key}
                type="button"
                className={`menu-sec${i === active ? ' on' : ''}${dragSec === i ? ' dragging' : ''}`}
                onClick={() => {
                  setActive(i);
                  setChecked(new Set());
                  setSelKey(null);
                }}
                draggable={editable}
                onDragStart={() => setDragSec(i)}
                onDragOver={(e) => {
                  if (dragSec === null || dragSec === i) return;
                  e.preventDefault();
                  const next = [...sections];
                  const [m] = next.splice(dragSec, 1);
                  next.splice(i, 0, m!);
                  change(next);
                  if (active === dragSec) setActive(i);
                  else if (active === i) setActive(dragSec);
                  setDragSec(i);
                }}
                onDragEnd={() => setDragSec(null)}
              >
                <Icon size={19} />
                <span className="menu-sec-name">{s.name || 'Untitled'}</span>
                <span className="pkg-count">{s.items.length}</span>
              </button>
            );
          })}
          {sections.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No sections yet. Start with Starters, Main Courses or Drinks.</div>}
        </div>

        <div className="card fill">
          {section ? (
            <>
              <div className="pkg-head">
                <span className="pkg-head-icon">
                  {(() => {
                    const Icon = sectionIcon(section.name);
                    return <Icon size={22} />;
                  })()}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <input
                    className="menu-sec-title"
                    value={section.name}
                    disabled={!editable}
                    maxLength={60}
                    placeholder="Section name"
                    onChange={(e) => change(sections.map((s, i) => (i === active ? { ...s, name: e.target.value } : s)))}
                  />
                  <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                    {items.length} dishes · click the name to rename the section
                  </span>
                </span>
                {editable && (
                  <button
                    className="btn danger-outline"
                    onClick={() =>
                      ask(`Delete the section ${section.name || 'Untitled'}? Its ${items.length} dishes are removed when you save.`, () => {
                        change(sections.filter((_, i) => i !== active));
                        setActive(Math.max(0, active - 1));
                      })
                    }
                  >
                    <Trash2 size={16} /> Delete section
                  </button>
                )}
              </div>
              <div className="filters" style={{ marginTop: 14 }}>
                <label className="search">
                  <Search size={16} />
                  <input placeholder="Search dishes in this section…" value={q} onChange={(e) => setQ(e.target.value)} />
                </label>
                <Select value={show} onChange={(e) => setShow(e.target.value)} aria-label="Availability">
                  <option value="all">All dishes</option>
                  <option value="available">Available</option>
                  <option value="hidden">Hidden</option>
                </Select>
                <Select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
                  <option value="order">Sort by order</option>
                  <option value="name">Sort by name</option>
                  <option value="price">Sort by price</option>
                </Select>
                {editable && (
                  <button className="btn primary" onClick={addDish}>
                    <Plus size={16} /> Add dish
                  </button>
                )}
              </div>
              {checked.size > 0 && editable && (
                <div className="bulk-bar">
                  <strong>{checked.size} selected</strong>
                  <button className="btn small" onClick={() => setItems(items.map((d) => (checked.has(d.key) ? { ...d, available: true } : d)))}>
                    Make available
                  </button>
                  <button className="btn small" onClick={() => setItems(items.map((d) => (checked.has(d.key) ? { ...d, available: false } : d)))}>
                    Hide
                  </button>
                  <button
                    className="btn small danger"
                    onClick={() =>
                      ask(`Delete ${checked.size} dishes? They are removed when you save.`, () => {
                        setItems(items.filter((d) => !checked.has(d.key)));
                        setChecked(new Set());
                      })
                    }
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                  <button className="icon-btn" style={{ marginLeft: 'auto' }} onClick={() => setChecked(new Set())} title="Clear selection">
                    <X size={16} />
                  </button>
                </div>
              )}
              {shown.length === 0 ? (
                <Empty>{items.length ? 'No dish matches.' : 'No dishes in this section yet. Use “Add dish”.'}</Empty>
              ) : (
                <div className="table-wrap">
                  <table className="fac-table dish-table">
                    <thead>
                      <tr>
                        <th style={{ width: 30 }} />
                        {editable && (
                          <th style={{ width: 30 }}>
                            <input
                              type="checkbox"
                              checked={allChecked}
                              aria-label="Select all"
                              onChange={() => setChecked(allChecked ? new Set() : new Set(pageKeys))}
                            />
                          </th>
                        )}
                        <th style={{ width: 30 }}>#</th>
                        <th>Dish</th>
                        <th style={{ width: 110 }}>Price ({venue.currency})</th>
                        <th>Availability</th>
                        <th>Allergens</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((d) => {
                        const i = items.indexOf(d);
                        return (
                          <tr
                            key={d.key}
                            className={`${sel?.key === d.key ? 'selected' : ''}${dragDish === i ? ' dragging' : ''}`}
                            onClick={() => setSelKey(d.key)}
                            draggable={canDrag}
                            onDragStart={(e) => {
                              if ((e.target as HTMLElement).closest('input')) return e.preventDefault();
                              setDragDish(i);
                            }}
                            onDragOver={(e) => {
                              if (dragDish === null || dragDish === i) return;
                              e.preventDefault();
                              const next = [...items];
                              const [m] = next.splice(dragDish, 1);
                              next.splice(i, 0, m!);
                              setItems(next);
                              setDragDish(i);
                            }}
                            onDragEnd={() => setDragDish(null)}
                          >
                            <td>
                              <span className="drag-handle" title={canDrag ? 'Drag to reorder' : 'Clear search, filter and sort to reorder'}>
                                <GripVertical size={16} />
                              </span>
                            </td>
                            {editable && (
                              <td onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={checked.has(d.key)}
                                  aria-label={`Select ${d.name}`}
                                  onChange={() =>
                                    setChecked((cur) => {
                                      const n = new Set(cur);
                                      if (n.has(d.key)) n.delete(d.key);
                                      else n.add(d.key);
                                      return n;
                                    })
                                  }
                                />
                              </td>
                            )}
                            <td className="muted">{i + 1}</td>
                            <td>
                              <span className="row" style={{ gap: 12, flexWrap: 'nowrap', alignItems: 'center' }}>
                                <DishPhoto url={url(d.imageAssetId)} disabled={!editable} onChange={(id) => setDish(d.key, { imageAssetId: id })} />
                                <span className="dish-text">
                                  <input
                                    data-dish={d.key}
                                    className="dish-name"
                                    value={d.name}
                                    placeholder="Dish name"
                                    disabled={!editable}
                                    maxLength={120}
                                    onChange={(e) => setDish(d.key, { name: e.target.value })}
                                  />
                                  <textarea
                                    className="dish-desc"
                                    value={d.description}
                                    placeholder="Ingredients or a short description"
                                    disabled={!editable}
                                    rows={2}
                                    maxLength={500}
                                    onChange={(e) => setDish(d.key, { description: e.target.value })}
                                  />
                                </span>
                              </span>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                className="dish-price"
                                inputMode="decimal"
                                value={d.price}
                                placeholder="—"
                                disabled={!editable}
                                onChange={(e) => setDish(d.key, { price: e.target.value.replace(/[^\d.]/g, '') })}
                                onBlur={() => d.price && !isNaN(Number(d.price)) && setDish(d.key, { price: Number(d.price).toFixed(2) })}
                              />
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <span className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                                <Toggle on={d.available} onChange={(v) => editable && setDish(d.key, { available: v })} label="Available" />
                                <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{d.available ? 'Available' : 'Hidden'}</span>
                              </span>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <AllergenInput value={d.allergens} disabled={!editable} onChange={(allergens) => setDish(d.key, { allergens })} />
                            </td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                              {editable && (
                                <>
                                  <button className="icon-btn" title="Duplicate" onClick={() => setItems([...items.slice(0, i + 1), { ...d, key: newKey(), name: `${d.name} (copy)` }, ...items.slice(i + 1)])}>
                                    <Copy size={16} />
                                  </button>
                                  <button className="icon-btn danger-icon" title="Delete" onClick={() => ask(`Delete ${d.name || 'this dish'}? It is removed when you save.`, () => setItems(items.filter((x) => x.key !== d.key)))}>
                                    <Trash2 size={16} />
                                  </button>
                                  {sections.length > 1 && (
                                    <Menu
                                      minWidth={200}
                                      button={({ onClick }) => (
                                        <button type="button" className="icon-btn" title="More" onClick={onClick}>
                                          <MoreHorizontal size={16} />
                                        </button>
                                      )}
                                    >
                                      {sections.map((s, si) =>
                                        si === active ? null : (
                                          <button key={s.key} type="button" className="more-item" onClick={() => moveDishTo(d.key, si)}>
                                            <ArrowDownUp size={15} /> Move to {s.name || 'Untitled'}
                                          </button>
                                        ),
                                      )}
                                    </Menu>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <Empty>Add a first section with “New section”, e.g. Starters.</Empty>
          )}
        </div>

        <DishPreview dish={sel} img={sel ? url(sel.imageAssetId) : null} currency={venue.currency} accent={((site.branding as Record<string, unknown>).accentColor as string) || '#6E4BFF'} venue={venue.name} section={section?.name ?? ''} />
      </div>
    </>
  );
}

function DishPhoto({ url, disabled, onChange }: { url: string | null; disabled: boolean; onChange: (id: string | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const upload = useMutation({
    mutationFn: (f: File) => api.upload<{ id: string }>('/assets', f),
    onSuccess: async (a) => {
      await qc.invalidateQueries({ queryKey: ['assets'] });
      onChange(a.id);
    },
  });
  return (
    <span className="dish-photo">
      <button
        type="button"
        className="fac-thumb dish"
        disabled={disabled || upload.isPending}
        title={url ? 'Change photo' : 'Add a photo'}
        onClick={(e) => {
          e.stopPropagation();
          input.current?.click();
        }}
      >
        {url ? <img src={url} alt="" /> : upload.isPending ? <Upload size={16} /> : <ImagePlus size={18} />}
      </button>
      {url && !disabled && (
        <button
          type="button"
          className="dish-photo-x"
          title="Remove photo"
          onClick={(e) => {
            e.stopPropagation();
            onChange(null);
          }}
        >
          <X size={11} />
        </button>
      )}
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
          e.target.value = '';
        }}
      />
    </span>
  );
}

/** Allergen chips with an inline input: type and press Enter or comma. */
function AllergenInput({ value, disabled, onChange }: { value: string[]; disabled: boolean; onChange: (v: string[]) => void }) {
  const [text, setText] = useState('');
  const add = () => {
    const t = text.trim().replace(/,$/, '');
    if (t && !value.some((v) => v.toLowerCase() === t.toLowerCase())) onChange([...value, t[0]!.toUpperCase() + t.slice(1)]);
    setText('');
  };
  return (
    <div className="allergen-box">
      {value.map((a) => {
        const [fg, bg] = tint(a);
        return (
          <span key={a} className="chip" style={{ color: fg, background: bg }}>
            {a}
            {!disabled && (
              <button type="button" onClick={() => onChange(value.filter((x) => x !== a))} aria-label={`Remove ${a}`}>
                <X size={10} />
              </button>
            )}
          </span>
        );
      })}
      {!disabled && (
        <input
          value={text}
          placeholder={value.length ? '' : 'None'}
          list="allergen-list"
          onChange={(e) => (e.target.value.endsWith(',') ? (setText(e.target.value), setTimeout(add)) : setText(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            } else if (e.key === 'Backspace' && !text && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={add}
        />
      )}
      <datalist id="allergen-list">
        {['Dairy', 'Gluten', 'Nuts', 'Shellfish', 'Egg', 'Fish', 'Soy', 'Sesame', 'Celery', 'Mustard'].map((a) => (
          <option key={a} value={a} />
        ))}
      </datalist>
    </div>
  );
}

function DishPreview({ dish, img, currency, accent, venue, section }: { dish: Dish | null; img: string | null; currency: string; accent: string; venue: string; section: string }) {
  const price = dish?.price ? `${currency} ${Number(dish.price).toFixed(2)}` : '';
  return (
    <div className="card dish-preview">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="row" style={{ gap: 8, fontWeight: 700 }}>
          <Tv size={18} color="var(--brand)" /> Live preview
        </span>
        {dish && (
          <span className={`state-pill ${dish.available ? 'online' : 'offline'}`}>
            <span className="state-dot" /> {dish.available ? 'On TV' : 'Hidden'}
          </span>
        )}
      </div>
      {dish ? (
        <>
          <div className="dish-hero">{img ? <img src={img} alt="" /> : <ImagePlus size={30} />}</div>
          <div className="row" style={{ justifyContent: 'space-between', marginTop: 14, flexWrap: 'nowrap', gap: 10, alignItems: 'baseline' }}>
            <h2 style={{ margin: 0, fontSize: 20 }}>{dish.name || 'New dish'}</h2>
            {price && <strong style={{ color: 'var(--brand)', fontSize: 17, whiteSpace: 'nowrap' }}>{price}</strong>}
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            {venue} · {section}
          </div>
          {dish.description && <p className="muted" style={{ fontSize: 14, lineHeight: 1.5, margin: '10px 0 0' }}>{dish.description}</p>}
          <div className="dish-meta">
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Allergens</div>
            <span className="row" style={{ gap: 6 }}>
              {dish.allergens.length ? (
                dish.allergens.map((a) => {
                  const [fg, bg] = tint(a);
                  return (
                    <span key={a} className="chip" style={{ color: fg, background: bg }}>
                      {a}
                    </span>
                  );
                })
              ) : (
                <span className="chip">None</span>
              )}
            </span>
          </div>
          <div className="dish-tv">
            <div className="dish-tv-card" style={{ borderColor: accent }}>
              <span className="dish-tv-img">{img ? <img src={img} alt="" /> : null}</span>
              <span style={{ minWidth: 0 }}>
                <strong>{dish.name || 'New dish'}</strong>
                <span>{dish.description}</span>
              </span>
              <em>{price}</em>
            </div>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 6, textAlign: 'center' }}>
              The dish as a row of the venue menu on the TV
            </div>
          </div>
        </>
      ) : (
        <div className="muted" style={{ marginTop: 16, fontSize: 13 }}>
          Click a dish to preview it.
        </div>
      )}
    </div>
  );
}
